/**
 * Frontend Environment Detection Utilities
 * Centralized environment detection for the entire frontend application
 *
 * NOTE: Frontend uses window.location.hostname (browser API)
 * Backend uses process.env (Node.js API) - they cannot be shared
 */

/**
 * Get environment name based on hostname
 */
export function getEnvironment(): 'development' | 'test' | 'production' {
  const hostname = window.location.hostname;

  // Development: localhost or *.localhost (e.g., demo.localhost)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
    return 'development';
  }

  if (hostname.includes('checkademic.dev')) {
    return 'test';
  }

  return 'production';
}

/**
 * Environment check helpers
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

export function isTest(): boolean {
  return getEnvironment() === 'test';
}

export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if current site is the admin portal
 */
export function isAdminSite(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'admin.checkademic.com' || hostname === 'admin.checkademic.dev' || hostname === 'admin.localhost';
}

/**
 * Extract subdomain from current hostname
 */
export function getSubdomain(): string | undefined {
  const hostname = window.location.hostname;

  if (hostname.includes('localhost')) {
    // Handle *.localhost format (e.g., demo.localhost)
    const parts = hostname.split('.');
    if (parts.length > 1 && parts[0] !== 'localhost') {
      return parts[0];
    }
  } else if (hostname.includes('checkademic.com') || hostname.includes('checkademic.dev')) {
    // Handle production/test domains (e.g., demo.checkademic.com)
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www') {
      return parts[0];
    }
  }

  return undefined;
}

/**
 * Get API base URL for the current environment
 */
export function getApiUrl(): string {
  const hostname = window.location.hostname;

  // Development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }

  // For subdomain-based requests on localhost, still use localhost:3000
  if (hostname.includes('localhost') && hostname !== 'localhost') {
    return 'http://localhost:3000';
  }

  // Production or Test - use same protocol and domain
  if (hostname.includes('checkademic.com') || hostname.includes('checkademic.dev')) {
    return `${window.location.protocol}//${hostname}`;
  }

  // Fallback - use relative URLs
  return '';
}

/**
 * Build a tenant URL (for any subdomain)
 * @param subdomain - Tenant subdomain
 * @param path - Optional path (default: empty)
 */
export function buildTenantUrl(subdomain: string, path: string = ''): string {
  // Normalize path to ensure it starts with / if provided
  const normalizedPath = path && !path.startsWith('/') ? `/${path}` : path;
  const env = getEnvironment();

  if (env === 'production') {
    return `https://${subdomain}.checkademic.com${normalizedPath}`;
  } else if (env === 'test') {
    return `https://${subdomain}.checkademic.dev${normalizedPath}`;
  } else {
    // Development: Use http:// for localhost (no SSL certificates)
    return `http://${subdomain}.localhost:5173${normalizedPath}`;
  }
}

/**
 * Get the parent portal base URL for the current environment
 *
 * Detects environment (localhost, test, production) and returns appropriate URL
 *
 * @param subdomain - Tenant subdomain
 * @returns Full base URL for parent portal (e.g., https://demo.checkademic.com)
 */
export function getParentPortalBaseUrl(subdomain: string): string {
  const hostname = window.location.hostname;

  // Local development - FIXED: Use http:// for localhost
  if (hostname.includes('localhost')) {
    return `http://${subdomain}.localhost:5173`;
  }

  // Test environment
  if (hostname.includes('checkademic.dev')) {
    return `https://${subdomain}.checkademic.dev`;
  }

  // Production
  return `https://${subdomain}.checkademic.com`;
}

/**
 * Get the parent registration URL for a specific link code
 *
 * @param subdomain - Tenant subdomain
 * @param linkCode - Parent link code (6-character hex)
 * @returns Full registration URL with code parameter
 */
export function getParentRegistrationUrl(subdomain: string, linkCode: string): string {
  const baseUrl = getParentPortalBaseUrl(subdomain);
  return `${baseUrl}/parent/register?code=${linkCode}`;
}
